/**
 * The round export «Expedientes (CSV)» (PRD #341): every expediente of the round
 * with its current verdict, served by `GET /rounds/{round_id}/export?format=csv`.
 *
 * The server owns the file (columns, Spanish labels, BOM, CRLF, Mexico City
 * dates) and names it through `Content-Disposition`; this module only fetches it
 * and resolves the filename. DEMO mode (no backend) synthesizes a sample with
 * the same columns and one row per state, through the same download path.
 */

/** The file's columns, in order — mirrors `COLUMNS` in `vera.api.round_export`. */
export const EXPORT_COLUMNS = [
  "Expediente",
  "Nombre",
  "CURP identificación",
  "CURP certificado",
  "Veredicto vigente",
  "Veredicto Vera",
  "Veredicto humano",
  "Revisado por",
  "Fecha revisión",
  "Prioridad",
  "Motivos de rechazo",
  "Observaciones",
  "Etiquetas",
  "Códigos",
  "Corrida",
  "Fecha validación",
  "Detalle de error",
  "audit_id",
] as const;

type ExportColumn = (typeof EXPORT_COLUMNS)[number];
type ExportRow = Partial<Record<ExportColumn, string>>;

/** The quoted `filename="…"` of a `Content-Disposition` header, or null. */
export function filenameFromDisposition(
  header: string | null | undefined,
): string | null {
  const match = header?.match(/filename="([^"]+)"/i);
  return match ? match[1] : null;
}

/** `AAAAMMDD-HHmm` of `now` in Mexico City — the server's cut-time stamp. */
function mexicoCityStamp(now: Date): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Mexico_City",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}${parts.month}${parts.day}-${parts.hour}${parts.minute}`;
}

/** The server's filename rule, used when the header is unreadable (and in DEMO). */
export function fallbackExportFilename(roundId: string, now: Date): string {
  const safeRound = roundId.replace(/[^A-Za-z0-9._-]+/g, "_");
  return `expedientes-${safeRound}-${mexicoCityStamp(now)}.csv`;
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** A DEMO sample: the real columns and one row per state (UTF-8 BOM + CRLF). */
export function demoRoundExportCsv(roundId: string): string {
  const rows: ExportRow[] = [
    {
      Expediente: `${roundId}-EXP-001`, Nombre: "MARÍA FERNANDA LÓPEZ PÉREZ",
      "CURP identificación": "LOPM050312MDFPRRA1", "CURP certificado": "LOPM050312MDFPRRA1",
      "Veredicto vigente": "Aprobado", "Veredicto Vera": "Aprobado",
      Corrida: "1", "Fecha validación": "2026-09-23 10:15", audit_id: "aud-demo-001",
    },
    {
      Expediente: `${roundId}-EXP-002`, Nombre: "JOSÉ ÁNGEL MUÑOZ RAMÍREZ",
      "CURP identificación": "MURJ040101HDFXMSA2", "CURP certificado": "MURJ040101HDFXMSA2",
      "Veredicto vigente": "Rechazado", "Veredicto Vera": "Rechazado",
      "Motivos de rechazo": "INE vencida", Códigos: "INE_EXPIRED",
      Corrida: "1", "Fecha validación": "2026-09-23 10:16", audit_id: "aud-demo-002",
    },
    {
      Expediente: `${roundId}-EXP-003`, Nombre: "ANA SOFÍA GARCÍA TORRES",
      "CURP identificación": "GATA060715MDFRRNA3", "CURP certificado": "GATA060715MDFRRNA9",
      "Veredicto vigente": "En revisión", "Veredicto Vera": "En revisión",
      Prioridad: "Alta", Observaciones: "CURP no coincide; Nombre no coincide",
      Etiquetas: "Menor de edad", Códigos: "CURP_MISMATCH; NAME_MISMATCH",
      Corrida: "1", "Fecha validación": "2026-09-23 10:17", audit_id: "aud-demo-003",
    },
    {
      Expediente: `${roundId}-EXP-004`, Nombre: "LUIS ALBERTO HERNÁNDEZ DÍAZ",
      "CURP identificación": "HEDL030920HDFRZSA4", "CURP certificado": "HEDL030920HDFRZSA4",
      "Veredicto vigente": "Aprobado", "Veredicto Vera": "En revisión",
      "Veredicto humano": "Aprobado", "Revisado por": "revisora@escuela.mx",
      "Fecha revisión": "2026-09-23 12:40",
      Observaciones: "Folio pendiente de validación", Códigos: "FOLIO_PENDING",
      Corrida: "2", "Fecha validación": "2026-09-23 11:02", audit_id: "aud-demo-004",
    },
    {
      Expediente: `${roundId}-EXP-005`, "Veredicto vigente": "Error",
      "Detalle de error": "TIMEOUT: el expediente excedió el tiempo de proceso",
    },
  ];
  const lines = [
    EXPORT_COLUMNS.join(","),
    ...rows.map((row) => EXPORT_COLUMNS.map((c) => csvCell(row[c] ?? "")).join(",")),
  ];
  return `﻿${lines.join("\r\n")}\r\n`;
}

/** What the export endpoint call hands back: the body and the raw response. */
export interface ExportResponse {
  data?: Blob;
  response: Response;
}

export interface FetchRoundExportDeps {
  demo: boolean;
  /** `GET /rounds/{round_id}/export?format=csv` as a Blob (throws on non-2xx). */
  get: (roundId: string) => Promise<ExportResponse>;
  now?: () => Date;
}

/** The export file of a round: the server's bytes and filename, or the DEMO sample. */
export async function fetchRoundExport(
  roundId: string,
  { demo, get, now = () => new Date() }: FetchRoundExportDeps,
): Promise<{ blob: Blob; filename: string }> {
  if (demo) {
    return {
      blob: new Blob([demoRoundExportCsv(roundId)], { type: "text/csv;charset=utf-8" }),
      filename: fallbackExportFilename(roundId, now()),
    };
  }
  const { data, response } = await get(roundId);
  return {
    blob: data!,
    filename:
      filenameFromDisposition(response.headers.get("content-disposition")) ??
      fallbackExportFilename(roundId, now()),
  };
}
